export const REP_VOICE_PROVIDERS = ['whisper', 'browser', 'auto', 'disabled'];

export const getRepVoiceProvider = value => {
  const provider = (value || 'browser').trim().toLowerCase();
  return REP_VOICE_PROVIDERS.includes(provider) ? provider : 'browser';
};

export const REP_AUDIO_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus'
];

export const isRepAppleMobileWebKit = navigatorLike => {
  if (!navigatorLike) return false;
  const userAgent = navigatorLike.userAgent || '';
  const platform = navigatorLike.platform || '';
  return /iPad|iPhone|iPod/i.test(userAgent)
    || (platform === 'MacIntel' && Number(navigatorLike.maxTouchPoints) > 1);
};

export const selectRepAudioMimeType = (MediaRecorderClass, navigatorLike = null) => {
  if (!MediaRecorderClass || typeof MediaRecorderClass.isTypeSupported !== 'function') return '';
  const mimeTypes = isRepAppleMobileWebKit(navigatorLike)
    ? ['audio/mp4', ...REP_AUDIO_MIME_TYPES.filter(type => type !== 'audio/mp4')]
    : REP_AUDIO_MIME_TYPES;
  return mimeTypes.find(type => MediaRecorderClass.isTypeSupported(type)) || '';
};

export const extensionForRepAudio = mimeType => {
  const baseType = (mimeType || '').split(';', 1)[0].toLowerCase();
  if (baseType === 'audio/mp4') return 'm4a';
  if (baseType === 'audio/ogg') return 'ogg';
  return 'webm';
};

const writeAscii = (view, offset, value) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

export const encodeRepAudioBufferAsWav = audioBuffer => {
  const sampleRate = audioBuffer.sampleRate;
  const frameCount = audioBuffer.length;
  const channelCount = Math.max(1, audioBuffer.numberOfChannels);
  const bytesPerSample = 2;
  const output = new ArrayBuffer(44 + frameCount * bytesPerSample);
  const view = new DataView(output);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + frameCount * bytesPerSample, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, frameCount * bytesPerSample, true);

  const channels = Array.from({length: channelCount}, (_item, index) => audioBuffer.getChannelData(index));
  let offset = 44;
  for (let frame = 0; frame < frameCount; frame += 1) {
    const mixed = channels.reduce((sum, channel) => sum + channel[frame], 0) / channelCount;
    const sample = Math.max(-1, Math.min(1, mixed));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += bytesPerSample;
  }
  return new Blob([output], {type: 'audio/wav'});
};

export const readRepAudioBlob = audio => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('AUDIO_READ_ERROR'));
  reader.readAsArrayBuffer(audio);
});

export const normalizeRepAudioForUpload = async (audio, mimeType, AudioContextClass) => {
  const baseType = (mimeType || '').split(';', 1)[0].toLowerCase();
  if (baseType !== 'audio/mp4') return {audio, mimeType, extension: extensionForRepAudio(mimeType)};
  if (!AudioContextClass) throw new Error('AUDIO_CONTEXT_UNAVAILABLE');

  const context = new AudioContextClass();
  try {
    const decoded = await context.decodeAudioData(await readRepAudioBlob(audio));
    return {audio: encodeRepAudioBufferAsWav(decoded), mimeType: 'audio/wav', extension: 'wav'};
  } finally {
    if (typeof context.close === 'function') await context.close();
  }
};
